// Curated, human-readable reference for the anomaly rules shown on the Alerts page.
// Source of truth for the logic: apps/worker/src/services/anomaly/r*.ts +
// default params seeded in migrations/0006_seed.sql. Kept in sync by hand — if a
// rule's threshold/logic changes in the worker, update the matching entry here.

export type RuleSeverity = 'high' | 'medium' | 'low' | 'mixed';

export interface AnomalyRuleDoc {
  code: string;
  name: string;
  /** Display severity. 'mixed' = depends on how far over threshold (e.g. R8). */
  severity: RuleSeverity;
  /** false = defined in DB but has no evaluator wired in, so it never fires. */
  active: boolean;
  /** Plain-language description of exactly what makes this rule fire. */
  trigger: string;
  /** Default thresholds (what ships in the seed). Admins may tune these. */
  defaults: string[];
}

export const ANOMALY_RULES: AnomalyRuleDoc[] = [
  {
    code: 'R1',
    name: 'Velocity — Tần suất giao dịch',
    severity: 'medium',
    active: true,
    trigger:
      'Một thẻ phát sinh quá nhiều giao dịch trong thời gian ngắn. Cảnh báo khi vượt số GD cho phép trong 1 giờ HOẶC trong 24 giờ.',
    defaults: ['Tối đa 10 GD/giờ', 'Tối đa 30 GD/ngày'],
  },
  {
    code: 'R2',
    name: 'High amount — Giao dịch lớn',
    severity: 'high',
    active: true,
    trigger: 'Một giao dịch đơn lẻ có số tiền ≥ ngưỡng.',
    defaults: ['Ngưỡng: 20.000.000 ₫'],
  },
  {
    code: 'R3',
    name: 'New merchant + high amount — Đối tác mới, tiền lớn',
    severity: 'high',
    active: true,
    trigger:
      'Giao dịch lớn tại một đối tác (merchant) mới xuất hiện gần đây. Cảnh báo khi merchant lần đầu giao dịch nằm trong khoảng "tuổi mới" VÀ số tiền ≥ ngưỡng.',
    defaults: ['Tuổi merchant ≤ 90 ngày', 'Ngưỡng: 5.000.000 ₫'],
  },
  {
    code: 'R4',
    name: 'New merchant repeat — Đối tác mới lặp lại',
    severity: 'high',
    active: false,
    trigger:
      'Đối tác mới phát sinh nhiều giao dịch dồn dập trong một tuần với tổng tiền lớn. (Đã định nghĩa trong cấu hình nhưng CHƯA kích hoạt — hiện không chạy.)',
    defaults: ['Tuổi merchant ≤ 90 ngày', '≥ 3 GD trong 7 ngày', 'Tổng ≥ 30.000.000 ₫'],
  },
  {
    code: 'R5',
    name: 'Off-hours — Ngoài giờ',
    severity: 'low',
    active: false,
    trigger:
      'Giao dịch xảy ra ngoài giờ hành chính hoặc vào cuối tuần (giờ theo múi +07:00). (Đã tắt — phát sinh quá nhiều cảnh báo với đặc thù chi tiêu của tổ chức.)',
    defaults: ['Khung đêm: 22h–6h', 'Tính cả cuối tuần'],
  },
  {
    code: 'R6',
    name: 'Spending spike — Tăng chi đột biến',
    severity: 'medium',
    active: true,
    trigger:
      'Tổng chi của thẻ trong tháng vượt mức nền (trung bình các tháng trước) nhân hệ số. Mỗi thẻ chỉ cảnh báo một lần mỗi tháng.',
    defaults: ['Mức nền: trung bình 3 tháng', 'Hệ số: 1.5×'],
  },
  {
    code: 'R7',
    name: 'Duplicate — Nghi trùng lặp',
    severity: 'medium',
    active: false,
    trigger:
      'Nghi ngờ bị tính tiền hai lần (double-charge): cùng merchant và cùng số tiền trong một khoảng thời gian ngắn. (Đã tắt — đặc thù có rất nhiều giao dịch trùng lặp hợp lệ.)',
    defaults: ['Cửa sổ: ±10 phút'],
  },
  {
    code: 'R8',
    name: 'Department budget — Ngân sách bộ phận',
    severity: 'mixed',
    active: true,
    trigger:
      'Chi tiêu của bộ phận trong tháng chạm ngưỡng % ngân sách. Mức "warn" khi đạt ngưỡng cảnh báo, mức "block" (nghiêm trọng) khi đạt 100% ngân sách. Chỉ áp dụng cho giao dịch đã gán bộ phận.',
    defaults: ['Warn: ≥ 80% ngân sách', 'Block: ≥ 100% ngân sách'],
  },
  {
    code: 'R9',
    name: 'Refund / decline — Hoàn tiền / Từ chối',
    severity: 'low',
    active: true,
    trigger: 'Theo dõi giao dịch hoàn tiền (refund) hoặc bị từ chối (decline).',
    defaults: ['Bật theo dõi'],
  },
];
